import React, { ReactNode, useState, useMemo, useCallback, useRef, useEffect } from "react"
import ReactDOM from "react-dom"
import Image from "next/image"

import { useUpdateableState, useWindowScrollOffset } from "@lib"

export interface DropdownOption {
    title: string
    value: string
}

const DropdownSelect: React.FunctionComponent<{
    className?: string,
    title?: string,
    placeholder?: string,
    multipleChoice?: boolean,
    options: DropdownOption[],
    value?: string | string[],
    onChange?: (value: string | string[]) => void,
    onChangeFilter?: (value: string) => void,
    optionRenderer?: (option: DropdownOption, isSelected: boolean) => ReactNode
}> = ({
    className,
    title,
    placeholder,
    multipleChoice,
    options,
    value,
    onChange,
    onChangeFilter,
    optionRenderer
}) => {
    // Internal stateful variable to hold on to selected value(s), that
    // can be updated if the input props `value` changes, or user changes
    // selection. Because this component supports multiple selection, opt to
    // use a string array storage instead of using string.
    const [selectedValue, setSelectedValue] = useUpdateableState<string[]>(
        options
            .filter((each) => {
                if (Array.isArray(value)) {
                    return (value || []).includes(each.value)
                } else {
                    return value === each.value
                }
            })
            .map((each) => each.value)
            .sort()
        , (current, next) => {
            return (current || []).join(",") !== (next || []).join(",")
        }
    )

    // List of selected DropdownOption based on options and selectedValue state.
    // We mainly use this value for display purposes (e.g. display the title
    // of selected value instead of the raw value).
    const selectedOption = useMemo(() => {
        return options.filter((each) => (selectedValue || []).includes(each.value) === true)
    }, [options, selectedValue])

    const [filterInputValue, setFilterInputValue] = useState("")

    const filterValueChangeHandler = useMemo(() => (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilterInputValue(event.target.value)
        onChangeFilter && onChangeFilter(event.target.value)
    }, [onChangeFilter])

    const matchingOptions = useMemo(() => {
        if (filterInputValue.length === 0) {
            return multipleChoice
                ? options.filter((each) => !(selectedValue || []).includes(each.value))
                : options
        }

        return multipleChoice
            ? options.filter((each) => each.title.toLocaleLowerCase().includes(filterInputValue.toLocaleLowerCase()) && !(selectedValue || []).includes(each.value))
            : options.filter((each) => each.title.toLocaleLowerCase().includes(filterInputValue.toLocaleLowerCase()))
    }, [options, filterInputValue, multipleChoice, selectedValue])

    const [isOptionListVisible, setIsOptionListVisible] = useState(false)

    // Ref to reference the filter <input> element.
    // We need this to set focus on the input element when user clicks on the
    // selection option display element.
    const inputRef = useRef<HTMLInputElement>(null)

    // We need dropdown rect to know where to position the dropdown
    const dropdownRef = useRef<HTMLDivElement>(null)
    const dropdownRect = dropdownRef.current?.getBoundingClientRect()

    const [scrollOffsetX, scrollOffsetY] = useWindowScrollOffset()

    const onSelectOption = useCallback((option: DropdownOption) => {
        const nextSelected = (selectedValue || []).concat(option.value)
        setSelectedValue(!multipleChoice ? [option.value] : nextSelected)

        // We are done filtering, so clear filter input
        setFilterInputValue("")
        onChangeFilter && onChangeFilter("")
        if (!multipleChoice || nextSelected.length === options.length) {
            // ...and hide the options list
            setIsOptionListVisible(false)
        }

        onChange && onChange(!multipleChoice ? option.value : nextSelected)

        // Focus on the dropdown element. We need to explicitly do this because
        // (dropdown option) button will capture focus.
        dropdownRef.current?.focus()
    }, [options, multipleChoice, selectedValue, setSelectedValue, onChange, onChangeFilter])

    const onRemoveOption = useCallback((option: DropdownOption) => {
        const nextSelected = (selectedValue || []).filter((each) => each !== option.value)
        setSelectedValue(nextSelected)

        // We are done filtering, so clear filter input
        setFilterInputValue("")
        onChangeFilter && onChangeFilter("")
        // ...and hide the options list
        setIsOptionListVisible(false)

        onChange && onChange(nextSelected)
    }, [selectedValue, setSelectedValue, onChange, onChangeFilter])

    const onClearSelection = useCallback(() => {
        setSelectedValue([])

        // We are done filtering, so clear filter input
        setFilterInputValue("")
        // ...and hide the options list
        setIsOptionListVisible(false)

        onChange && onChange(!multipleChoice ? "" : [])
    }, [multipleChoice, setSelectedValue, onChange])

    const dropdownListClass = useMemo(() => {
        return [
            "absolute left-0 right-0 mt-[4px] max-h-[70vh] z-20",
            "overflow-auto overscroll-contain",
            "flex flex-col",
            "bg-white-900 border border-gray-200 rounded shadow-lg",
            isOptionListVisible ? "origin-top scale-y-100" : "hidden origin-top scale-y-0",
            "transition ease-in-out duration-200"
        ].join(" ")
    }, [isOptionListVisible])

    const modalPortalRef = useRef<HTMLElement>()
    // Run this effect only once (empty dependencies list) to hold on to
    // modalPortal div element (if it exists).
    useEffect(() => {
        if (typeof window !== undefined) {
            modalPortalRef.current = document.getElementById("modalPortal") || undefined
        }
    }, [])

    // Side effect to scroll selected value into view
    const selectedOptionButtonRef = useRef<HTMLButtonElement>(null)
    useEffect(() => {
        if (!isOptionListVisible || !selectedOptionButtonRef.current) {
            return
        }
        selectedOptionButtonRef.current?.scrollIntoView({ block: "center" })
    }, [isOptionListVisible])

    return (
        // Giving div a non-undefined tabIndex makes it focusable. We need
        // this so that onBlur() of this div can be triggered, to hide options
        // list when users click on anything that is not contained within this
        // div.
        <div ref={dropdownRef} className={className} tabIndex={-1}
            onBlur={(event) => {
                // Tapping anywhere outside of this component should
                // collapse option list.
                // currentTarget: element that triggered event (loses focus)
                // relatedTarget: Child of current target that gained focus
                if (modalPortalRef.current && modalPortalRef.current.contains(event.relatedTarget)) {
                    return
                }
                if (event.relatedTarget === null || !event.currentTarget.contains(event.relatedTarget)) {
                    setIsOptionListVisible(false)
                }
            }}>

            <div className="relative">
                {title && <label className="block text-footnote text-black-700 text-left mb-[8px]">{title}</label>}

                <div className="relative py-[12px] px-[16px] flex border border-black-500 rounded">
                    {selectedOption && filterInputValue.length === 0 &&
                        <div className="flex flex-wrap my-auto gap-x-[4px] gap-y-[4px]" onClick={() => inputRef.current?.focus()}>
                            {
                                selectedOption.map((each) => {
                                    if (multipleChoice) {
                                        return (
                                            <div key={each.value} className="text-subhead text-primary-900 bg-primary-100">
                                                <span className="p-[4px]">{each.title}</span>
                                                <button type="button" className="p-[2px] hover:text-alert-600 hover:bg-alert-100"
                                                    onClick={() => onRemoveOption(each)}>
                                                    <i className="fa-fw fa-solid fa-xmark"></i>
                                                </button>
                                            </div>
                                        )
                                    } else {
                                        return <div key={each.value} className="text-body text-black-900">{each.title}</div>
                                    }
                                })
                            }
                        </div>
                    }

                    <input ref={inputRef} type="text" className="w-0 min-w-0 flex-auto outline-none text-body text-black-900"
                        value={filterInputValue}
                        placeholder={selectedOption ? "" : placeholder}
                        onChange={filterValueChangeHandler}
                        onFocus={() => {
                            if (!isOptionListVisible) {
                                setIsOptionListVisible(true)
                            }
                        }} />

                    <button type="button" className={["w-[30px] transition duration-300", isOptionListVisible ? "rotate-180" : "rotate-0"].join(" ")} onClick={() => {
                        setIsOptionListVisible((isVisible) => !isVisible)
                        if (!isOptionListVisible) {
                            inputRef?.current?.focus()
                        }
                    }}>
                        <Image width={24} height={24} objectFit="contain" src="/images/ic_chevron_down_accent.svg" alt="Dropdown Icon" />
                    </button>
                </div>

                {modalPortalRef.current && dropdownRect &&
                    // Use React portal to render the dropdown list as a child of
                    // div with ID "modalPortal" (a sibling of root div, see _document.tsx).
                    // The reason we want to do this is so that if this DropdownSelect
                    // component can "escape" any parent in the DOM tree that has
                    // overflow-clip or overflow-hidden (which will cause parts
                    // of this component to be clipped away otherwise) set.
                    ReactDOM.createPortal(
                        <div className={dropdownListClass} style={{
                            left: `${dropdownRect.left}px`,
                            top: `${dropdownRect.top + (dropdownRect.height || 0)}px`,
                            width: `${dropdownRect.width}px`,
                            transform: `translate(-${scrollOffsetX}, -${scrollOffsetY})`
                        }}>
                            {matchingOptions.map((each) => {
                                if ((selectedValue || []).includes(each.value)) {
                                    return (
                                        <button type="button"
                                            ref={selectedOptionButtonRef}
                                            key={each.value} className="px-[16px] py-[12px] text-left text-body bg-primary-100 text-black-900 hover:bg-primary-100 hover:text-black-900" onClick={() => onSelectOption(each)}>
                                            {each.title}
                                        </button>
                                    )
                                } else {
                                    return (
                                        <button type="button"
                                            key={each.value} className="px-[16px] py-[12px] text-left text-body hover:bg-black-50" onClick={() => onSelectOption(each)}>
                                            {each.title}
                                        </button>
                                    )
                                }

                            })}
                        </div>,
                        modalPortalRef.current
                    )
                }
            </div>
        </div>
    )
}

export default DropdownSelect
